import { ApiResponse } from "@axl/membership-sdk";
import { ArgsProps } from "antd/lib/notification";
import { ReactNode, useCallback, useEffect, useState } from "react";

import { isDev } from "../is-dev";

import { useNotifier } from "./useNotifier";

export type ApiMutateConfig<TResponseData> = {
  onSuccess?: (data: TResponseData, result: ApiResponse<TResponseData>) => void;
  onError?: (result: ApiResponse<TResponseData>) => void;
  mock?: TResponseData;
  auto?: boolean;
  successMsg?: ReactNode;
  notify?: {
    successOpts?: ArgsProps;
    errorOpts?: ArgsProps;
  };
};

export const useApiRequest = <
  TResponseData,
  T extends (...args: unknown[]) => Promise<ApiResponse<TResponseData>>,
>(
  cb: T,
  config?: ApiMutateConfig<TResponseData>,
): [
  T,
  {
    data: TResponseData | null;
    loading: boolean;
    state: "idle" | "loading" | "success" | "error";
    result: ApiResponse<TResponseData> | null;
  },
] => {
  const [data, setData] = useState<TResponseData | null>(null);
  const [result, setResult] = useState<ApiResponse<TResponseData> | null>(null);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<"idle" | "loading" | "success" | "error">(
    "idle",
  );

  const [notify] = useNotifier();
  const execute = useCallback(
    async (...args: unknown[]) => {
      setState("loading");
      setLoading(true);
      setResult(null);
      const cbResult = await cb(...args);
      setResult(cbResult);
      const bodyData = config?.mock && isDev ? config?.mock : cbResult.body;
      if (cbResult.success) {
        setData(bodyData);
        setState("success");
      } else {
        setState("error");
      }

      setLoading(false);

      notify(
        cbResult,
        config?.successMsg,
        config?.notify?.successOpts,
        config?.notify?.errorOpts,
      );
      if (cbResult.success) {
        config?.onSuccess?.(bodyData, cbResult);
      } else {
        config?.onError?.(cbResult);
      }

      return cbResult;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cb],
  ) as T;

  useEffect(() => {
    if (config?.auto === true) execute();
  }, [config?.auto, execute]);

  return [execute, { data, loading, state, result }];
};
